import {
  devicePurposes,
  readPurpose,
  writePurpose,
  type DevicePurpose,
  type PurposeBody,
  type PurposeContext,
} from '@/api/core/device-purpose';
import {
  readNativeCatalog,
  readNativeProperties,
  type NativePage,
  type NativeProperty,
} from '@/api/core/native-property-read';
import { DirtyGuard } from '@/components/dirty-guard';
import { Button, FormField, Input } from '@mgrvtechgroup/admin-ds';
import { useContext, useEffect, useId, useRef, useState } from 'react';
import type { Location } from 'react-router';
import { Choice } from '../property/presentation/choice';
import { allowed, commandOwner, epoch } from '../property/presentation/command-owner';
import { useCurrentCommand } from '../property/presentation/use-current-command';
import type { PropertyRuntime } from '../property/property-queries';
import { propertyRead } from '../property/property-queries';
import { PurposeDraftContext } from './purpose-draft-context';
export function DevicePurposeEditor({
  runtime,
  id,
  propertyId,
  deviceRevision,
  guardNavigation = false,
  guardLeaves,
}: {
  runtime: PropertyRuntime;
  id: string;
  propertyId?: string;
  deviceRevision?: number;
  guardNavigation?: boolean;
  /** 주어지면 같은 화면 안의 주소 변경 중 이 함수가 "떠남"이라 본 것만 막는다(지점 상세의 탭 전환). 없으면 검색 인자 변경도 막는다. */
  guardLeaves?: (current: Location, next: Location) => boolean;
}) {
  const [selectedProperty, setSelectedProperty] = useState(propertyId ?? '');
  const [cursor, setCursor] = useState<string | null>(null);
  const [properties, setProperties] = useState<NativePage<NativeProperty[]>>();
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [lookupError, setLookupError] = useState('');
  useEffect(() => {
    queueMicrotask(() => {
      setSelectedProperty(propertyId ?? '');
      setProperties(undefined);
      setLabels({});
      setLookupError('');
      setCursor(null);
    });
  }, [id, propertyId, runtime]);
  const cmd = useCurrentCommand<PurposeContext & { selectedProperty: string }, PurposeBody>({
    runtime,
    scope: `purpose:${id}`,
    bits: 2,
    readKey: selectedProperty,
    read: async () => {
      const value = await readPurpose(runtime.client, id, selectedProperty || undefined);
      const attempt = commandOwner(runtime, `purpose:${id}`).attempt;
      const accepted = attempt?.accepted ? (attempt.body as PurposeBody) : undefined;
      if (
        accepted &&
        (value.purpose !== accepted.purpose || value.revision < accepted.expected_revision)
      )
        throw new Error('저장된 용도가 요청과 다릅니다. 현재 용도를 다시 조회해 주세요.');
      return { ...value, selectedProperty };
    },
    write: (body: PurposeBody) => writePurpose(runtime.client, id, body),
    validate: async (body?: PurposeBody) => {
      const p = body?.property_id ?? selectedProperty;
      const c = await readPurpose(runtime.client, id, p || undefined);
      if (
        p &&
        (c.requires_property_selection ||
          c.command_property_ids.length !== 1 ||
          c.command_property_ids[0] !== p)
      )
        throw new Error('현재 지점의 장비 연결 범위를 확인하지 못했습니다.');
    },
  });
  const lookupEpoch = epoch(runtime);
  const candidateIds = cmd.current?.command_property_ids.join(',') ?? '';
  const needsSelection =
    !propertyId &&
    (!!cmd.current?.requires_property_selection ||
      candidateIds.split(',').filter(Boolean).length > 1 ||
      !!selectedProperty);
  useEffect(() => {
    let live = true;
    const generation = runtime.session.getSnapshot().generation;
    const current = () => live && epoch(runtime) === lookupEpoch && allowed(runtime, 2);
    if (!needsSelection || !current()) return;
    const load = async () => {
      try {
        if (cmd.current?.requires_property_selection || properties) {
          const page = await propertyRead(
            runtime,
            generation,
            () => readNativeProperties(runtime.client, cursor, 'asc'),
            undefined,
            current,
          );
          if (!current()) return;
          setProperties(page);
          setLabels((previous) => ({
            ...previous,
            ...Object.fromEntries(page.data.map((p) => [p.id, `${p.name} · ${p.code}`])),
          }));
        } else {
          const entries = await Promise.all(
            candidateIds
              .split(',')
              .filter(Boolean)
              .map(async (p) => {
                const catalog = await propertyRead(
                  runtime,
                  generation,
                  () => readNativeCatalog(runtime.client, p, null),
                  undefined,
                  current,
                );
                if (catalog.data.property.id !== p)
                  throw new Error('지점 범위를 확인하지 못했습니다.');
                return [
                  p,
                  `${catalog.data.property.name} · ${catalog.data.property.code}`,
                ] as const;
              }),
          );
          if (current()) setLabels((previous) => ({ ...previous, ...Object.fromEntries(entries) }));
        }
        if (current()) setLookupError('');
      } catch {
        if (current())
          setLookupError('작업 지점 정보를 확인하지 못했습니다. 현재 용도를 다시 조회해 주세요.');
      }
    };
    void load();
    return () => {
      live = false;
    };
    // Scope and authorization changes cancel the previous lookup completion.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime, id, lookupEpoch, needsSelection, candidateIds, cursor]);
  const [purpose, setPurpose] = useState<DevicePurpose>('UNKNOWN');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const currentValue = cmd.current;
  const [refreshingRevision, setRefreshingRevision] = useState<number>();
  useEffect(() => {
    if (
      deviceRevision === undefined ||
      !currentValue ||
      currentValue.revision >= deviceRevision ||
      purpose !== currentValue.purpose ||
      reason.length > 0 ||
      cmd.attempt ||
      cmd.busy
    )
      return;
    setRefreshingRevision(deviceRevision);
    void cmd.refresh().finally(() => {
      setRefreshingRevision((current) => (current === deviceRevision ? undefined : current));
    });
    // A registry revision may refresh a pristine panel, never rebase an existing draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceRevision]);
  useEffect(() => {
    queueMicrotask(() => {
      if (currentValue) {
        setPurpose(currentValue.purpose);
        setReason('');
        setConfirm(false);
      }
    });
  }, [currentValue]);
  const c = cmd.current;
  const confirmRegion = useRef<HTMLDivElement>(null);
  const confirmTrigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirm) {
      confirmRegion.current?.focus();
      if (typeof confirmRegion.current?.scrollIntoView === 'function')
        confirmRegion.current.scrollIntoView({ block: 'start' });
    }
  }, [confirm]);
  // 저장하지 않은 입력이나 결과 미확정 요청이 있으면 목록에 알려, 목록이 이 행을 치우기 전에 확인을 받게 한다.
  const reportDraft = useContext(PurposeDraftContext);
  const editorKey = useId();
  const hasInput = !!c && (purpose !== c.purpose || reason.length > 0);
  const hasRequest = !!c && !!cmd.attempt;
  useEffect(() => {
    reportDraft?.(
      editorKey,
      hasInput || hasRequest ? { input: hasInput, request: hasRequest } : null,
    );
    return () => {
      reportDraft?.(editorKey, null);
    };
  }, [reportDraft, editorKey, hasInput, hasRequest]);
  // 권한이 바뀌어 현재값을 잃으면 입력과 보내려던 요청은 보안 경계상 폐기된다. 말없이 사라지지 않게 알린다.
  const hadDraft = useRef(false);
  const [discardedNotice, setDiscardedNotice] = useState(false);
  useEffect(() => {
    if (c) {
      if (!hadDraft.current) setDiscardedNotice(false);
    } else if (hadDraft.current) {
      setDiscardedNotice(true);
    }
    hadDraft.current = hasInput || hasRequest;
  }, [c, hasInput, hasRequest]);
  const locked = cmd.busy || !!cmd.attempt || !cmd.canWrite || refreshingRevision !== undefined;
  const targetProperty =
    selectedProperty || (c?.command_property_ids.length === 1 ? c.command_property_ids[0] : '');
  return (
    <section aria-label="장비 용도" className="space-y-3 rounded border p-3">
      {guardNavigation && (
        <DirtyGuard
          when={!!c && (purpose !== c.purpose || reason.length > 0 || !!cmd.attempt)}
          includeSearch={!guardLeaves}
          leaves={guardLeaves}
        />
      )}
      <h3 className="font-semibold">장비 용도</h3>
      {!cmd.canWrite && <p>장비 용도 조회·변경에는 현재 지점 수정 권한이 필요합니다.</p>}
      {discardedNotice && !c && (
        <p role="alert">
          권한이나 작업자가 바뀌어 입력 중이던 내용과 보내려던 요청을 폐기했습니다. 그 요청이 서버에
          반영됐는지는 이 화면에서 확인하지 못했으니, 권한을 확인한 뒤 현재 용도를 다시 조회해
          주세요.
        </p>
      )}
      <p>저장된 용도·설정은 출입 권한 부여나 실제 온라인 연결을 뜻하지 않습니다.</p>
      {cmd.error && <p role="alert">{cmd.error}</p>}
      {cmd.success && <p role="status">저장 후 현재 장비 용도를 다시 확인했습니다.</p>}
      <Button disabled={cmd.busy} variant="outline" onClick={() => void cmd.refresh()}>
        현재 용도 다시 조회
      </Button>
      {c && (
        <>
          <p>현재 용도: {devicePurposes[c.purpose]}</p>
          <p>
            현재 연결:{' '}
            {c.requires_property_selection
              ? '연결 없음 · 작업 지점 선정 필요'
              : c.command_property_ids.length
                ? '지점 연결 확인'
                : '연결 지점 없음'}
          </p>
          <p>실제 장비·공급자 상태: 이 조회에서 확인하지 않음</p>
          <p>마지막 용도 변경 사유: {c.last_change_reason ?? '기록 없음'}</p>
          {needsSelection && (
            <label>
              작업 지점{' '}
              <Choice
                label="용도 변경 지점"
                value={selectedProperty || 'choose'}
                disabled={locked}
                options={{
                  choose: '지점 선택',
                  ...labels,
                }}
                onChange={(v) => {
                  if (v !== 'choose') {
                    setSelectedProperty(v);
                    setConfirm(false);
                  }
                }}
              />
            </label>
          )}
          {lookupError && <p role="alert">{lookupError}</p>}
          {needsSelection && properties?.next_cursor && (
            <Button
              variant="outline"
              disabled={locked}
              onClick={() => {
                setCursor(properties.next_cursor);
              }}
            >
              다음 지점 50개
            </Button>
          )}
          <label>
            변경할 용도{' '}
            <Choice
              label="변경할 용도"
              value={purpose}
              disabled={locked}
              options={devicePurposes}
              onChange={(v) => {
                setPurpose(v as DevicePurpose);
                setConfirm(false);
              }}
            />
          </label>
          <FormField label="용도 변경 사유" htmlFor={`purpose-reason-${id}`}>
            <Input
              id={`purpose-reason-${id}`}
              value={reason}
              disabled={locked}
              maxLength={500}
              onChange={(e) => {
                setReason(e.target.value);
                setConfirm(false);
              }}
            />
          </FormField>
          <Button
            ref={confirmTrigger}
            disabled={
              locked ||
              !targetProperty ||
              c.selectedProperty !== selectedProperty ||
              (!!selectedProperty &&
                (c.requires_property_selection ||
                  c.command_property_ids.length !== 1 ||
                  c.command_property_ids[0] !== selectedProperty)) ||
              !reason.trim()
            }
            onClick={() => {
              setConfirm(true);
            }}
          >
            용도 변경 확인
          </Button>
          {confirm && (
            <div
              ref={confirmRegion}
              tabIndex={-1}
              role="region"
              aria-label="용도 변경 확인"
              className="scroll-mt-48"
            >
              <p>
                {devicePurposes[c.purpose]} → {devicePurposes[purpose]} · 사유: {reason}
              </p>
              <p>
                현재 지점의 장비 목적만 변경합니다. 그룹이나 현재 유효 사용자는 이 기능에서 변경하지
                않습니다.
              </p>
              <Button
                disabled={locked}
                onClick={() =>
                  void cmd.submit({
                    command_id: crypto.randomUUID(),
                    property_id: targetProperty ?? '',
                    expected_revision: c.revision,
                    purpose,
                    reason,
                  })
                }
              >
                확인하고 용도 저장
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setConfirm(false);
                  confirmTrigger.current?.focus();
                }}
              >
                용도 수정 계속
              </Button>
            </div>
          )}
        </>
      )}
      {cmd.attempt && !cmd.attempt.accepted && (
        <>
          <p role="alert">
            원래 요청을 보존했습니다. 결과를 확인하기 전 새 요청을 보내지 않습니다.
          </p>
          <Button disabled={cmd.busy} onClick={() => void cmd.submit()}>
            원래 용도 변경 결과 확인
          </Button>
        </>
      )}
    </section>
  );
}
